import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faPlus, faTrash, faEdit, faFileCsv,
  faCheckCircle, faExclamationTriangle,
  faHandHoldingDollar, faWallet, faCoins, faChartLine,
  faSyncAlt, faEraser
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import ManageEmployesModal from './ManageEmployesModal';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

// ============================================================
// 1. CACHE DEXIE (24h) avec fallback périmé
// ============================================================
const db = new Dexie('FichesPaieCacheDB');
db.version(1).stores({
  payrolls: 'key, data, timestamp',
  stats: 'key, data, timestamp'
});
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCached(key, store = 'payrolls', allowExpired = false) {
  try {
    const entry = await db[store].get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCached(key, data, store = 'payrolls') {
  try {
    await db[store].put({ key, data, timestamp: Date.now() });
  } catch { /* ignore */ }
}
async function getStale(key, store = 'payrolls') {
  return getCached(key, store, true);
}

// ============================================================
// 2. DÉDOUBLONNAGE DES REQUÊTES EN COURS
// ============================================================
const pendingFetches = new Map();

// ============================================================
// 3. CONFIGURATION API & UTILITAIRE
// ============================================================
const API_URL = `${API_CONFIG2.apiUrl}paie/api_fichepaie.php`;

const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }
  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// ============================================================
// 4. COMPOSANT PRINCIPAL
// ============================================================
const ManageFichesPaie = ({ userId }) => {
  // --- États ---
  const [payrolls, setPayrolls] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshingPayrolls, setRefreshingPayrolls] = useState(false);
  const [loadingStats, setLoadingStats] = useState(true);
  const [refreshingStats, setRefreshingStats] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 10 });
  const [dataSource, setDataSource] = useState('cache');
  const [statsSource, setStatsSource] = useState('cache');

  // Modales
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showpayFullModal, setShowpayFullModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingPayroll, setEditingPayroll] = useState(null);
  const [formData, setFormData] = useState({
    id: '', employeId: '', periode: '', salaireBase: 0, primes: 0, cotisations: 0, netAPayer: 0, statut: 'Payé'
  });
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [selectedFile, setSelectedFile] = useState(null);

  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const abortControllerStatsRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const isMounted = useRef(true);

  // ============================================================
  // 5. GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((message, variant = 'success', duration = 5000) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, variant });
    toastTimeoutRef.current = setTimeout(() => {
      if (isMounted.current) setToast({ show: false });
      toastTimeoutRef.current = null;
    }, duration);
  }, []);

  // ============================================================
  // 6. RÉCUPÉRATION DES FICHES DE PAIE (avec cache, dédoublonnage, annulation)
  // ============================================================
  const fetchPayrolls = useCallback(async (page = 1, search = '', forceRefresh = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = `payrolls_${page}_${search}`;
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage
    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setPayrolls(result.data);
          setPagination(result.pagination);
          setDataSource('cache (partagé)');
          setLoading(false);
          setRefreshingPayrolls(false);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshingPayrolls(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    if (forceRefresh) setRefreshingPayrolls(true);
    else setLoading(true);
    setDataSource('');

    const fetchPromise = (async () => {
      try {
        // 1. Cache
        if (!forceRefresh) {
          const cached = await getCached(cacheKey, 'payrolls');
          if (cached) {
            if (isMounted.current) {
              setPayrolls(cached.data);
              setPagination(cached.pagination);
              setDataSource('cache');
              setLoading(false);
              setRefreshingPayrolls(false);
            }
            return cached;
          }
        }

        // 2. Requête réseau
        const url = `${API_URL}?action=fetch&page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}`;
        const response = await fetch(url, { signal: controller.signal });

        // Gestion 429
        if (response.status === 429) {
          const stale = await getStale(cacheKey, 'payrolls');
          if (stale) {
            if (isMounted.current) {
              setPayrolls(stale.data);
              setPagination(stale.pagination);
              setDataSource('cache (périmé)');
              showToast('429 – Utilisation du cache (données peut-être obsolètes)', 'warning');
              setLoading(false);
              setRefreshingPayrolls(false);
            }
            return stale;
          }
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          showToast(`429 – Nouvel essai dans ${waitTime/1000}s`, 'warning');
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchPayrolls(page, search, forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await getStale(cacheKey, 'payrolls');
          if (stale) {
            if (isMounted.current) {
              setPayrolls(stale.data);
              setPagination(stale.pagination);
              setDataSource('cache (périmé)');
              showToast('Erreur réseau – Utilisation du cache', 'warning');
              setLoading(false);
              setRefreshingPayrolls(false);
            }
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (data.status === 'success') {
          const result = { data: data.data, pagination: data.pagination };
          if (isMounted.current) {
            setPayrolls(data.data);
            setPagination(data.pagination);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            await setCached(cacheKey, result, 'payrolls');
            if (forceRefresh) {
              showToast('Données actualisées', 'success');
            }
            setLoading(false);
            setRefreshingPayrolls(false);
          }
          return result;
        } else {
          throw new Error(data.message || 'Erreur serveur');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        const stale = await getStale(cacheKey, 'payrolls');
        if (stale && isMounted.current) {
          setPayrolls(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          showToast('Erreur – Utilisation du cache', 'warning');
          setLoading(false);
          setRefreshingPayrolls(false);
          return stale;
        }
        if (isMounted.current) {
          showToast(err.message || 'Erreur de chargement', 'danger');
          setLoading(false);
          setRefreshingPayrolls(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [pagination.limit, showToast]);

  // ============================================================
  // 7. RÉCUPÉRATION DES STATISTIQUES (avec cache, dédoublonnage, annulation)
  // ============================================================
  const fetchStats = useCallback(async (forceRefresh = false, retryCount = 0) => {
    if (abortControllerStatsRef.current) {
      abortControllerStatsRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerStatsRef.current = controller;

    const cacheKey = 'stats_global';
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setStats(result);
          setStatsSource('cache (partagé)');
          setLoadingStats(false);
          setRefreshingStats(false);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    if (retryCount > 5) {
      if (isMounted.current) {
        setLoadingStats(false);
        setRefreshingStats(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    if (forceRefresh) setRefreshingStats(true);
    else setLoadingStats(true);
    setStatsSource('');

    const fetchPromise = (async () => {
      try {
        if (!forceRefresh) {
          const cached = await getCached(cacheKey, 'stats');
          if (cached) {
            if (isMounted.current) {
              setStats(cached);
              setStatsSource('cache');
              setLoadingStats(false);
              setRefreshingStats(false);
            }
            return cached;
          }
        }

        const url = `${API_URL}?action=stats`;
        const response = await fetch(url, { signal: controller.signal });

        if (response.status === 429) {
          const stale = await getStale(cacheKey, 'stats');
          if (stale) {
            if (isMounted.current) {
              setStats(stale);
              setStatsSource('cache (périmé)');
              showToast('429 – Utilisation du cache (statistiques)', 'warning');
              setLoadingStats(false);
              setRefreshingStats(false);
            }
            return stale;
          }
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          showToast(`429 – Nouvel essai (statistiques) dans ${waitTime/1000}s`, 'warning');
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchStats(forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await getStale(cacheKey, 'stats');
          if (stale) {
            if (isMounted.current) {
              setStats(stale);
              setStatsSource('cache (périmé)');
              showToast('Erreur réseau – Utilisation du cache (statistiques)', 'warning');
              setLoadingStats(false);
              setRefreshingStats(false);
            }
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (data.status === 'success') {
          if (isMounted.current) {
            setStats(data);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setStatsSource(source);
            await setCached(cacheKey, data, 'stats');
            if (forceRefresh) {
              showToast('Statistiques actualisées', 'success');
            }
            setLoadingStats(false);
            setRefreshingStats(false);
          }
          return data;
        } else {
          throw new Error(data.message || 'Erreur serveur');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        const stale = await getStale(cacheKey, 'stats');
        if (stale && isMounted.current) {
          setStats(stale);
          setStatsSource('cache (périmé)');
          showToast('Erreur – Utilisation du cache (statistiques)', 'warning');
          setLoadingStats(false);
          setRefreshingStats(false);
          return stale;
        }
        if (isMounted.current) {
          showToast(err.message || 'Erreur de chargement des statistiques', 'danger');
          setLoadingStats(false);
          setRefreshingStats(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerStatsRef.current === controller) {
        abortControllerStatsRef.current = null;
      }
    }
  }, [showToast]);

  // ============================================================
  // 8. VIDER LE CACHE ET FORCER L'ACTUALISATION
  // ============================================================
  const handleClearCache = useCallback(async () => {
    if (loading || loadingStats || refreshingPayrolls || refreshingStats) return;
    try {
      // Supprimer toutes les entrées de cache pour les fiches de paie et les stats
      await db.payrolls.clear();
      await db.stats.clear();
      // Vider également le Map des requêtes en attente
      pendingFetches.clear();
      showToast('Cache vidé avec succès', 'success');
      // Forcer le rechargement complet
      await refreshAll();
    } catch (err) {
      showToast('Erreur lors du vidage du cache', 'danger');
    }
  }, [loading, loadingStats, refreshingPayrolls, refreshingStats, showToast]);

  // ============================================================
  // 9. RAFRAÎCHISSEMENT GLOBAL
  // ============================================================
  const refreshAll = useCallback(() => {
    fetchPayrolls(pagination.currentPage, searchTerm, true);
    fetchStats(true);
  }, [fetchPayrolls, fetchStats, pagination.currentPage, searchTerm]);

  // ============================================================
  // 10. EFFETS : INITIALISATION ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchPayrolls(1, '', false);
    fetchStats(false);
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (abortControllerStatsRef.current) abortControllerStatsRef.current.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      pendingFetches.clear();
    };
  }, []);

  // ============================================================
  // 11. RECHERCHE (debounce)
  // ============================================================
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchPayrolls(1, val, false);
    }, 400);
  };

  // ============================================================
  // 12. CRUD HANDLERS (avec rechargement forcé après succès)
  // ============================================================
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const method = editingPayroll ? 'PUT' : 'POST';
    const action = editingPayroll ? 'update' : 'create';
    try {
      const data = await fetchJSON(`${API_URL}?action=${action}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowModal(false);
        refreshAll();
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      showToast(err.message, 'danger');
    }
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(`${API_URL}?action=delete&id=${formData.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowDeleteModal(false);
        refreshAll();
      } else {
        showToast(data.message, 'danger');
      }
    } catch (err) {
      showToast('Erreur serveur', 'danger');
    }
  };

  const handleImportCSV = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      showToast('Veuillez sélectionner un fichier CSV', 'danger');
      return;
    }
    const formDataFile = new FormData();
    formDataFile.append('file', selectedFile);
    formDataFile.append('userId', userId);
    try {
      const res = await fetch(`${API_URL}?action=import`, { method: 'POST', body: formDataFile });
      const data = await res.json();
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowImportModal(false);
        setShowpayFullModal(false);
        setSelectedFile(null);
        refreshAll();
      } else {
        showToast(data.message, 'danger');
      }
    } catch (err) {
      showToast('Erreur lors de l\'import', 'danger');
    }
  };

  const openEditModal = (item) => {
    setEditingPayroll(item);
    setFormData({ ...item, salaireBase: parseFloat(item.salaireBase), primes: parseFloat(item.primes), cotisations: parseFloat(item.cotisations), netAPayer: parseFloat(item.netAPayer) });
    setShowModal(true);
  };

  const openPayModal = () => {
    setShowpayFullModal(true);
  };

  // ============================================================
  // 13. CONFIGURATION DES GRAPHIQUES
  // ============================================================
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: '#e0e7ff', boxWidth: 8, usePointStyle: true, font: { size: 10 } }
      }
    },
    scales: {
      y: { grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#94a3b8' } },
      x: { grid: { display: false }, ticks: { color: '#94a3b8' } }
    }
  };

  const getDonutData = (data) => {
    if (!data || data.length === 0) return { labels: [], datasets: [] };
    const colors = ['#3b82f6', '#f59e0b', '#ef4444'];
    return {
      labels: data.map(d => d.statut),
      datasets: [{
        data: data.map(d => d.count),
        backgroundColor: colors.slice(0, data.length),
        borderColor: '#0b1428',
        borderWidth: 2
      }]
    };
  };

  const getBarData = (data) => {
    if (!data || data.length === 0) return { labels: [], datasets: [] };
    return {
      labels: data.map(d => d.period),
      datasets: [
        {
          label: 'Net payé',
          data: data.map(d => parseFloat(d.totalNet)),
          backgroundColor: '#3b82f6',
          borderRadius: 6
        },
        {
          label: 'Primes',
          data: data.map(d => parseFloat(d.totalPrimes)),
          backgroundColor: '#60a5fa',
          borderRadius: 6
        }
      ]
    };
  };

  // ============================================================
  // 14. AFFICHAGE DU CHARGEMENT
  // ============================================================
  if (loading && !payrolls.length && !refreshingPayrolls) {
    return (
      <div className="monetra-page text-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="text-secondary mt-2">Chargement des fiches de paie...</p>
      </div>
    );
  }

  // ============================================================
  // 15. RENDU
  // ============================================================
  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – MANAGE FICHES PAIE (mobile optimisé)
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .monetra-page {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: 24px;
          padding: clamp(1rem, 1.5vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 24px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
          display: inline-block;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 0.9vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.4;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.danger {
          border-color: #ef4444;
          color: #f87171;
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.12);
        }
        .monetra-btn.outline-secondary {
          border-color: rgba(255,255,255,0.08);
          color: var(--text-secondary);
        }
        .monetra-btn.outline-secondary:hover:not(:disabled) {
          background: rgba(255,255,255,0.05);
        }
        .monetra-btn.p-2 {
          padding: 6px 10px;
          min-width: 36px;
          justify-content: center;
        }

        .search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 16px;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 180px;
          max-width: 100%;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        .glass-table {
          width: 100%;
          border-collapse: collapse;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
        }
        .glass-table th {
          padding: 12px 10px;
          text-align: left;
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
          font-weight: 600;
        }
        .glass-table td {
          padding: 10px 10px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          vertical-align: middle;
        }
        .glass-table tr {
          transition: 0.2s;
        }
        .glass-table tr:hover {
          background: rgba(255,255,255,0.03);
        }

        .status-badge {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: clamp(0.6rem, 0.8vw, 0.7rem);
          font-weight: 600;
        }
        .status-badge.payé {
          background: rgba(59, 130, 246, 0.15);
          color: var(--accent-blue);
        }
        .status-badge.en-attente {
          background: rgba(245, 158, 11, 0.15);
          color: #f59e0b;
        }
        .status-badge.impayé {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        .monetra-modal .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
          padding: clamp(1rem, 1.5vw, 1.5rem) clamp(1.2rem, 2vw, 2rem);
        }
        .monetra-modal .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
          padding: clamp(0.8rem, 1.5vw, 1.2rem) clamp(1.2rem, 2vw, 2rem);
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }
        .monetra-modal .modal-body {
          padding: clamp(1.2rem, 2vw, 2rem);
        }

        .monetra-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 14px;
          color: var(--text-primary);
          padding: 10px 14px;
          width: 100%;
          transition: 0.3s;
          font-size: clamp(0.85rem, 1.1vw, 1rem);
        }
        .monetra-input:focus {
          border-color: var(--accent-blue);
          outline: none;
          background: rgba(255,255,255,0.06);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.06);
        }
        .monetra-input::placeholder {
          color: var(--text-muted);
        }
        .monetra-input option {
          background: #0b1428;
          color: var(--text-primary);
        }

        .toast-monetra {
          background: rgba(10, 20, 45, 0.8) !important;
          backdrop-filter: blur(24px) !important;
          -webkit-backdrop-filter: blur(24px) !important;
          border: 1px solid rgba(59, 130, 246, 0.2) !important;
          border-radius: 16px !important;
          color: var(--text-primary) !important;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6) !important;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }

        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-success { color: var(--accent-blue); }
        .text-danger { color: #ef4444; }
        .text-info { color: var(--accent-blue); }
        .fw-bold { font-weight: 600; }
        .small { font-size: clamp(0.65rem, 0.8vw, 0.85rem); }
        .m-0 { margin: 0; }
        .mb-4 { margin-bottom: clamp(1rem, 2vw, 1.5rem); }
        .me-2 { margin-right: 0.5rem; }
        .p-2 { padding: 0.5rem; }
        .p-3 { padding: 1rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .gap-2 { gap: 0.5rem; }
        .gap-3 { gap: 1rem; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .text-end { text-align: right; }
        .text-center { text-align: center; }
        .overflow-hidden { overflow: hidden; }
        .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .border-top { border-top: 1px solid rgba(255,255,255,0.04); }
        .border-opacity-10 { opacity: 0.1; }
        .mt-3 { margin-top: 1rem; }

        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(clamp(150px, 22vw, 220px), 1fr));
          gap: clamp(12px, 2vw, 20px);
          margin-bottom: 20px;
        }
        .kpi-card {
          background: var(--card-bg);
          border: 1px solid var(--card-border);
          border-radius: 20px;
          padding: 1.2rem 1rem;
          text-align: center;
          transition: all 0.2s ease;
        }
        .kpi-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          transform: translateY(-2px);
        }
        .kpi-value {
          font-size: clamp(1.6rem, 3vw, 1.8rem);
          font-weight: 700;
          margin-top: 6px;
          color: var(--accent-blue);
        }
        .kpi-label {
          color: var(--text-secondary);
          font-size: clamp(0.75rem, 1vw, 0.85rem);
          font-weight: 500;
        }

        .charts-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(12px, 2vw, 20px);
        }
        .chart-box {
          height: clamp(180px, 30vh, 220px);
          position: relative;
        }

        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-primary);
          opacity: 0.5;
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover {
          opacity: 1;
        }

        @media (max-width: 768px) {
          .monetra-page { padding: 12px; }
          .glass-card { padding: 1rem; }
          .charts-grid { grid-template-columns: 1fr; }
          .glass-table th, .glass-table td { font-size: 0.7rem; padding: 6px 8px; }
          .monetra-btn { font-size: 0.75rem; padding: 6px 12px; min-height: 38px; }
          .d-flex.justify-content-between.align-items-center {
            flex-direction: column;
            align-items: stretch !important;
            gap: 12px;
          }
          .d-flex.justify-content-between.align-items-center > div:first-child {
            text-align: center;
          }
          .d-flex.gap-2 {
            flex-wrap: wrap;
            justify-content: center;
          }
          .search-wrapper {
            max-width: 100%;
            min-width: unset;
          }
          .glass-table th:last-child, .glass-table td:last-child {
            min-width: 80px;
          }
          .monetra-modal .modal-footer {
            flex-direction: column-reverse;
          }
          .monetra-modal .modal-footer button {
            width: 100%;
            justify-content: center;
          }
        }
        @media (max-width: 480px) {
          .monetra-btn { font-size: 0.7rem; padding: 4px 10px; min-height: 34px; }
          .glass-table th, .glass-table td { font-size: 0.6rem; padding: 4px 6px; }
          .kpi-grid { grid-template-columns: 1fr 1fr; }
          .kpi-value { font-size: 1.4rem; }
          .chart-box { height: 160px; }
        }
      `}</style>

      <div className="monetra-page">
        {/* Header avec badges source et boutons */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="fw-bold mb-1" style={{ fontSize: 'clamp(1.6rem, 4vw, 2.2rem)' }}>
              Gestion des Fiches de Paie
            </h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              Gérez les paiements, primes et cotisations des employés.
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <span className={`badge-source ${dataSource === 'live' || dataSource === 'live (forcé)' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button className="monetra-btn" onClick={refreshAll} disabled={loading || loadingStats || refreshingPayrolls || refreshingStats}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || loadingStats} /> Actualiser
            </button>
            <button 
              className="monetra-btn danger" 
              onClick={handleClearCache} 
              disabled={loading || loadingStats || refreshingPayrolls || refreshingStats}
              title="Vider le cache et forcer le rechargement"
            >
              <FontAwesomeIcon icon={faEraser} /> Vider le cache
            </button>
            <button className="monetra-btn" onClick={() => setShowImportModal(true)} disabled={loading || loadingStats}>
              <FontAwesomeIcon icon={faFileCsv} /> Importer CSV
            </button>
            <button className="monetra-btn primary" onClick={openPayModal} disabled={loading || loadingStats}>
              <FontAwesomeIcon icon={faPlus} /> Ajouter
            </button>
          </div>
        </div>

        {/* SECTION ANALYSES & STATISTIQUES */}
        {loadingStats && !stats && !refreshingStats ? (
          <div className="glass-card text-center py-4">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : stats && (
          <div className="glass-card">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h5 className="fw-bold" style={{ color: 'var(--accent-blue)', fontSize: 'clamp(1rem, 1.4vw, 1.2rem)' }}>
                <FontAwesomeIcon icon={faChartLine} className="me-2" /> Analyses Rapides
              </h5>
              <span className={`badge-source ${statsSource === 'live' || statsSource === 'live (forcé)' ? 'live' : statsSource === 'cache (périmé)' ? 'warning' : ''}`}>
                {statsSource === 'cache' ? '📦 Cache' :
                 statsSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
                 statsSource === 'cache (partagé)' ? '🔄 Partage' :
                 statsSource === 'live' ? '🔄 Live' :
                 statsSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
              </span>
            </div>

            <div className="kpi-grid">
              <div className="kpi-card">
                <div className="kpi-label">Total Fiches</div>
                <div className="kpi-value">{stats.totals.totalCount}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Masse Salariale Totale</div>
                <div className="kpi-value">{parseFloat(stats.totals.totalNet).toFixed(2)}$</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Total Primes</div>
                <div className="kpi-value" style={{ color: 'var(--text-primary)' }}>{parseFloat(stats.totals.totalPrimes).toFixed(2)}$</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Total Cotisations</div>
                <div className="kpi-value" style={{ color: 'var(--text-primary)' }}>{parseFloat(stats.totals.totalCotisations).toFixed(2)}$</div>
              </div>
            </div>

            <div className="charts-grid mt-3">
              <div className="chart-box">
                <div className="text-secondary mb-2" style={{ fontSize: 'clamp(0.8rem, 1.1vw, 0.9rem)' }}>Répartition par Statut</div>
                <Doughnut data={getDonutData(stats.byStatus)} options={chartOptions} />
              </div>
              <div className="chart-box">
                <div className="text-secondary mb-2" style={{ fontSize: 'clamp(0.8rem, 1.1vw, 0.9rem)' }}>Évolution par Période</div>
                <Bar data={getBarData(stats.byPeriod)} options={chartOptions} />
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="glass-card d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div className="search-wrapper" style={{ minWidth: '260px', flex: 1, maxWidth: '100%' }}>
            <FontAwesomeIcon icon={faSearch} className="text-secondary" />
            <input
              type="text"
              placeholder="Rechercher (ID employé, période, statut...)"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="text-secondary small">
            <span>{pagination.totalRecords} enregistrements</span>
          </div>
        </div>

        {/* Table Container */}
        <div className="glass-card p-0 overflow-hidden mt-4">
          <div className="table-responsive">
            <table className="glass-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>EmployéID</th>
                  <th>Nom Complet</th>
                  <th>Période</th>
                  <th>Base</th>
                  <th>Primes</th>
                  <th>Cotisations</th>
                  <th>Net à payer</th>
                  <th>Statut</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && !refreshingPayrolls && payrolls.length === 0 ? (
                  <tr><td colSpan="10" className="text-center py-5"><div className="spinner-border text-primary" role="status" /></td></tr>
                ) : payrolls.length === 0 ? (
                  <tr><td colSpan="10" className="text-center py-5 text-secondary">Aucune fiche de paie trouvée.</td></tr>
                ) : (
                  payrolls.map(item => (
                    <tr key={item.id}>
                      <td><span className="fw-bold text-secondary">#{item.id}</span></td>
                      <td><span className="text-secondary">{item.employeId}</span></td>
                      <td><span className="text-secondary">{item.prenom} {item.nom}</span></td>
                      <td><span className="text-secondary">{item.periode}</span></td>
                      <td><span className="text-secondary">{parseFloat(item.salaireBase).toFixed(2)}$</span></td>
                      <td><span className="text-secondary">{parseFloat(item.primes).toFixed(2)}$</span></td>
                      <td><span className="text-secondary">{parseFloat(item.cotisations).toFixed(2)}$</span></td>
                      <td><strong>{parseFloat(item.netAPayer).toFixed(2)}$</strong></td>
                      <td>
                        <span className={`status-badge ${item.statut.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(' ', '-')}`}>
                          {item.statut}
                        </span>
                      </td>
                      <td className="text-end">
                        <button className="monetra-btn p-2 me-1" onClick={() => openEditModal(item)} disabled={loading || refreshingPayrolls}>
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button className="monetra-btn danger p-2" onClick={() => { setFormData(item); setShowDeleteModal(true); }} disabled={loading || refreshingPayrolls}>
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-3 border-top border-secondary border-opacity-10 d-flex justify-content-between align-items-center flex-wrap">
            <span className="text-secondary small">Page {pagination.currentPage} sur {pagination.totalPages}</span>
            <div className="d-flex gap-2">
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage <= 1 || loading || refreshingPayrolls}
                onClick={() => fetchPayrolls(pagination.currentPage - 1, searchTerm, false)}
              >
                ‹
              </button>
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage >= pagination.totalPages || loading || refreshingPayrolls}
                onClick={() => fetchPayrolls(pagination.currentPage + 1, searchTerm, false)}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* --- MODALE AJOUT / MODIFICATION --- */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="monetra-modal">
        <Form onSubmit={handleFormSubmit}>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: 'clamp(1.1rem, 2vw, 1.3rem)' }}>
              <FontAwesomeIcon icon={faEdit} className="text-success me-2" />
              {editingPayroll ? 'Modifier' : 'Ajouter'} un paiement d'agent
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="text-secondary small">ID Employé</label>
                <input className="monetra-input" name="employeId" value={formData.employeId} onChange={(e) => setFormData({ ...formData, employeId: e.target.value })} required disabled={loading || refreshingPayrolls} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Période (ex: 06/2026)</label>
                <input className="monetra-input" name="periode" value={formData.periode} onChange={(e) => setFormData({ ...formData, periode: e.target.value })} required disabled={loading || refreshingPayrolls} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Salaire de base ($)</label>
                <input type="number" step="0.01" className="monetra-input" name="salaireBase" value={formData.salaireBase} onChange={(e) => setFormData({ ...formData, salaireBase: parseFloat(e.target.value) })} required disabled={loading || refreshingPayrolls} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Primes ($)</label>
                <input type="number" step="0.01" className="monetra-input" name="primes" value={formData.primes} onChange={(e) => setFormData({ ...formData, primes: parseFloat(e.target.value) })} required disabled={loading || refreshingPayrolls} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Cotisations ($)</label>
                <input type="number" step="0.01" className="monetra-input" name="cotisations" value={formData.cotisations} onChange={(e) => setFormData({ ...formData, cotisations: parseFloat(e.target.value) })} required disabled={loading || refreshingPayrolls} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Net à payer ($)</label>
                <input type="number" step="0.01" className="monetra-input" name="netAPayer" value={formData.netAPayer} onChange={(e) => setFormData({ ...formData, netAPayer: parseFloat(e.target.value) })} required disabled={loading || refreshingPayrolls} />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Statut</label>
                <select className="monetra-input" name="statut" value={formData.statut} onChange={(e) => setFormData({ ...formData, statut: e.target.value })} disabled={loading || refreshingPayrolls}>
                  <option className="bg-dark" value="Payé">Payé</option>
                  <option className="bg-dark" value="En attente">En attente</option>
                  <option className="bg-dark" value="Impayé">Impayé</option>
                </select>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)} className="monetra-btn outline-secondary" disabled={loading || refreshingPayrolls}>Annuler</Button>
            <Button variant="success" type="submit" className="monetra-btn primary" disabled={loading || refreshingPayrolls}>Enregistrer</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showpayFullModal} onHide={() => setShowpayFullModal(false)} centered className="monetra-modal" size="xl">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 'clamp(1.1rem, 2vw, 1.3rem)' }}>
            <FontAwesomeIcon icon={faHandHoldingDollar} className="text-success me-2" />
            Ajouter un paiement d'agent
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ManageEmployesModal userId={userId} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowpayFullModal(false)} className="monetra-btn outline-secondary">Annuler</Button>
        </Modal.Footer>
      </Modal>

      {/* --- MODALE SUPPRESSION --- */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="monetra-modal">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger"><FontAwesomeIcon icon={faExclamationTriangle} /> Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer la fiche de paie de l'employé <strong>{formData.employeId}</strong> (période {formData.periode}) ?
          <br /><span className="text-secondary small">Cette action est irréversible.</span>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} className="monetra-btn outline-secondary" disabled={loading || refreshingPayrolls}>Annuler</Button>
          <Button variant="danger" onClick={handleDelete} className="monetra-btn danger" disabled={loading || refreshingPayrolls}>Confirmer la suppression</Button>
        </Modal.Footer>
      </Modal>

      {/* --- MODALE IMPORTATION CSV --- */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} centered className="monetra-modal">
        <Form onSubmit={handleImportCSV}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faFileCsv} className="text-info me-2" />
              Importer des données CSV
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-secondary small">Le fichier doit avoir les colonnes dans cet ordre :<br />
              <code>employeId, periode, salaireBase, primes, cotisations, netAPayer, statut</code>
            </p>
            <input type="file" accept=".csv" className="monetra-input" onChange={(e) => setSelectedFile(e.target.files[0])} required disabled={loading || refreshingPayrolls} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowImportModal(false)} className="monetra-btn outline-secondary" disabled={loading || refreshingPayrolls}>Annuler</Button>
            <Button variant="success" type="submit" className="monetra-btn primary" disabled={loading || refreshingPayrolls}>Importer</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* --- TOAST DE NOTIFICATION --- */}
      <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
        <div className={`toast-monetra p-3 ${toast.show ? 'd-block' : 'd-none'}`} style={{ minWidth: 'clamp(200px, 30vw, 300px)' }}>
          <div className="d-flex align-items-center gap-3">
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
            />
            <span style={{ fontSize: 'clamp(0.85rem, 1.2vw, 1rem)' }}>{toast.message}</span>
            <button
              className="btn-close-custom ms-auto"
              onClick={() => setToast({ ...toast, show: false })}
              style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', opacity: 0.5, fontSize: '1.2rem' }}
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageFichesPaie;